import * as React from "react";

import cornerstoneTools from "cornerstone-tools";
import cornerstone from "cornerstone-core";
import BrushIcon from "@mui/icons-material/Brush";
import {
  Button,
  Slider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
} from "@mui/material";
import Drawer from "@mui/material/Drawer";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { saveUserSettings } from "../actions/dcm_actions";
import AlertDialog from "../../../Components/Dialog";
import { RgbaStringColorPicker } from "react-colorful";
import { syncFluidEraseDiameter } from "../tools/AiSegmentationEraser";

const getModule = cornerstoneTools.getModule;
const { setters } = getModule("segmentation");

export default function BrushToolSettings(props) {
  const dispatch = useDispatch();
  let { search } = useLocation();
  const dicom_data = useSelector((state) => state.dcm);
  const query = new URLSearchParams(search);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const brushSettings = useSelector((state) => state.dcm).settings.brush;

  const screenshotSettings = useSelector((state) => state.dcm).settings
    .screenshot;
  const [respMessage, setRespMessage] = React.useState({
    error: false,
    message: "",
  });

  const onChangeRadius = (e, value) => {
    dispatch({ type: "SET_BRUSH_BORDER_RADIUS", borderRadius: value });
    setters.radius(value);
  };

  const onFluidEraserDiameterChange = (_e, value) => {
    dispatch({ type: "SET_FLUID_ERASER_DIAMETER", diameter: value });
    syncFluidEraseDiameter(value);

    const bscanEl = dicom_data?.dicomRefBscan?.current;
    if (
      dicom_data.selectedTool === "FluidSegmentationEraser" &&
      bscanEl
    ) {
      cornerstone.updateImage(bscanEl);
    }
  };

  const [activeLabelMap, setActiveLabelMap] = React.useState(0);

  const toggleDrawer = (type) => {
    setOpenDrawer(type);
  };

  const changeLabelMap = (index) => {
    dispatch({ type: "SET_BRUSH_SEGMENTATION_INDEX", index: index });
  };

  React.useEffect(() => {
    setters.activeLabelmapIndex(activeLabelMap);
  }, [activeLabelMap]);

  const segmentColors = [
    "rgb(221, 84, 84)",
    "rgb(77, 228, 121)",
    "rgb(166, 70, 235)",
    "rgb(189, 180, 116)",
    "rgb(109, 182, 196)",
    "rgb(204, 101, 157)",
    "rgb(123, 211, 94)",
    "rgb(93, 87, 218)",
  ];
  const handleScreenshotChange = (option) => {
    const updatedSettings = {
      irBscan: option === "irBscan",
      onlyIr: option === "onlyIr",
      onlyBscan: option === "onlyBscan",
    };
    dispatch({
      type: "SET_SCREENSHOT_OPTION",
      payload: updatedSettings,
    });
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Button
          onClick={() => toggleDrawer(true)}
          variant="contained"
          color="secondary"
          size="small"
          startIcon={<BrushIcon />}
        >
          Settings
        </Button>
      </div>

      <React.Fragment>
        <Drawer
          anchor={"right"}
          open={openDrawer}
          onClose={() => toggleDrawer(false)}
        >
          <div
            style={{
              padding: "10px",
              minWidth: "400px",
            }}
          >
            <h3>Brush Settings</h3>
            <div style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>
              <h4>Brush Size</h4>
              {/* Add slider */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  minHeight: "50px",
                }}
              >
                <div
                  style={{
                    minWidth: "120px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      height: brushSettings?.radius / 2,
                      width: brushSettings?.radius / 2,
                      borderRadius: "50%",
                      border: "1px solid red",
                      backgroundColor: "#F9AEAE",
                    }}
                  ></div>
                </div>
                <div
                  style={{
                    display: "flex",
                    width: "80%",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Slider
                    onChange={(e, v) => onChangeRadius(e, v)}
                    value={brushSettings?.radius}
                    min={1}
                    max={100}
                  />
                </div>
              </div>
            </div>

            <div style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>
              <h4>Fluid segmentation eraser size</h4>
              <p style={{ margin: "0 0 8px", fontSize: 13, opacity: 0.85 }}>
                Diameter in image pixels — only affects the fluid segmentation
                eraser; the hollow ring matches the erased area.
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  minHeight: "50px",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    width: Math.min(
                      100,
                      Math.max(
                        12,
                        (brushSettings?.fluidEraserDiameter ?? 20) * 0.5
                      )
                    ),
                    height: Math.min(
                      100,
                      Math.max(
                        12,
                        (brushSettings?.fluidEraserDiameter ?? 20) * 0.5
                      )
                    ),
                    borderRadius: "50%",
                    border: "2px solid rgba(255,255,255,0.95)",
                    backgroundColor: "transparent",
                    boxSizing: "border-box",
                  }}
                  title={`Diameter ${brushSettings?.fluidEraserDiameter ?? 20}px`}
                />
                <div
                  style={{
                    display: "flex",
                    width: "80%",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Slider
                    onChange={onFluidEraserDiameterChange}
                    value={
                      brushSettings?.fluidEraserDiameter != null
                        ? brushSettings.fluidEraserDiameter
                        : 20
                    }
                    min={6}
                    max={120}
                    valueLabelDisplay="auto"
                  />
                  <span style={{ fontSize: 12 }}>
                    {brushSettings?.fluidEraserDiameter ?? 20} px diameter (Ctrl
                    + mouse wheel on OPT)
                  </span>
                </div>
              </div>
            </div>
            <h3>LabelMap</h3>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                maxWidth: "400px",
                flexWrap: "wrap",
                justifyContent: "left",
                padding: "10px",
              }}
            >
              {segmentColors.map((color, index) => {
                return (
                  <div
                    key={`seg-color-${index}`}
                    style={{
                      width: "40px",
                      height: "40px",
                      cursor: "pointer",
                      backgroundColor: color,
                      border: "1px solid #ccc",
                      margin: "10px",
                    }}
                    onClick={() => changeLabelMap(index + 1)}
                  ></div>
                );
              })}
            </div>
            {/* <h3>Masks</h3>
            <div style={{ padding: "10px" }}>
              <input
                type="checkbox"
                onChange={() =>
                  dispatch({
                    type: "SET_BRUSH_SHOW_MASKS",
                    showMasks: !brushSettings.showMasks,
                  })
                }
                checked={brushSettings.showMasks}
              />
            </div> */}

            <h3>Screenshot</h3>
            <RadioGroup
              value={
                screenshotSettings?.irBscan
                  ? "irBscan"
                  : screenshotSettings?.onlyIr
                  ? "onlyIr"
                  : "onlyBscan"
              }
              onChange={(e) => handleScreenshotChange(e.target.value)}
            >
              <FormControlLabel
                control={<Radio />}
                value="irBscan"
                label="IR + B-scan"
              />
              <FormControlLabel
                control={<Radio />}
                value="onlyIr"
                label="Only IR"
              />
              <FormControlLabel
                control={<Radio />}
                value="onlyBscan"
                label="Only B-scan"
              />
            </RadioGroup>

            <h3>ETDRS</h3>
            <div style={{ padding: "10px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={brushSettings?.showEtdrsMirroring}
                    onChange={() =>
                      dispatch({
                        type: "SET_BRUSH_SHOW_ETDRS_MIRRORING",
                        showEtdrsMirroring: !brushSettings?.showEtdrsMirroring,
                      })
                    }
                  />
                }
                label="Show ETDRS line mirroring on B-scan"
              />
            </div>
            <h3>Segmentation</h3>

            <div
              style={{
                padding: "10px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {dicom_data?.aiSegmentationAvailable && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        dicom_data?.settings?.Segmentation?.aiSegmentation
                      }
                      onChange={() =>
                        dispatch({
                          type: "SET_AI_SEGMENTATION",
                          aiSegmentation:
                            !dicom_data?.settings?.Segmentation?.aiSegmentation,
                        })
                      }
                    />
                  }
                  label="AI-driven Segmentation"
                />
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      dicom_data?.settings?.Segmentation?.nativeSegmentation
                    }
                    onChange={() =>
                      dispatch({
                        type: "SET_NATIVE_SEGMENTATION",
                        nativeSegmentation:
                          !dicom_data?.settings?.Segmentation
                            ?.nativeSegmentation,
                      })
                    }
                  />
                }
                label="Native Segmentation"
              />
            </div>

            <h3>Fluid Segmentation</h3>
            <div
              style={{
                padding: "10px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Visibility toggles */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      dicom_data?.settings?.FluidSegmentation?.IRF ?? true
                    }
                    onChange={() =>
                      dispatch({
                        type: "SET_FLUID_SEGMENTATION_VISIBILITY_GLOBAL",
                        label: "IRF",
                        value: !(
                          dicom_data?.settings?.FluidSegmentation?.IRF ?? true
                        ),
                      })
                    }
                  />
                }
                label="Intraretinal Fluid (IRF)"
              />
              {/* IRF Color Picker */}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      dicom_data?.settings?.FluidSegmentation?.SRF ?? true
                    }
                    onChange={() =>
                      dispatch({
                        type: "SET_FLUID_SEGMENTATION_VISIBILITY_GLOBAL",
                        label: "SRF",
                        value: !(
                          dicom_data?.settings?.FluidSegmentation?.SRF ?? true
                        ),
                      })
                    }
                  />
                }
                label="Subretinal Fluid (SRF)"
              />
              {/* SRF Color Picker */}

              {/* <div style={{ minWidth: 140 }}>SRF Color</div>
                <div style={{ width: 220 }}>
                  <RgbaStringColorPicker
                    color={
                      dicom_data?.settings?.FluidSegmentationColors?.SRF ||
                      "rgba(255, 215, 0, 1)"
                    }
                    onChange={(color) =>
                      dispatch({
                        type: "SET_FLUID_SEGMENTATION_COLOR",
                        label: "SRF",
                        color,
                      })
                    }
                  />
                </div> */}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      dicom_data?.settings?.FluidSegmentation?.PED ?? true
                    }
                    onChange={() =>
                      dispatch({
                        type: "SET_FLUID_SEGMENTATION_VISIBILITY_GLOBAL",
                        label: "PED",
                        value: !(
                          dicom_data?.settings?.FluidSegmentation?.PED ?? true
                        ),
                      })
                    }
                  />
                }
                label="Pigmented Epithelial Detachment (PED)"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      dicom_data?.settings?.FluidSegmentation?.SHRM ?? true
                    }
                    onChange={() =>
                      dispatch({
                        type: "SET_FLUID_SEGMENTATION_VISIBILITY_GLOBAL",
                        label: "SHRM",
                        value: !(
                          dicom_data?.settings?.FluidSegmentation?.SHRM ?? true
                        ),
                      })
                    }
                  />
                }
                label="Subretinal Hyperreflective Material (SHRM)"
              />
              {/* PED Color Picker */}

              {/* <div style={{ minWidth: 140 }}>PED Color</div>
                <div style={{ width: 220 }}>
                  <RgbaStringColorPicker
                    color={
                      dicom_data?.settings?.FluidSegmentationColors?.PED ||
                      "rgba(0, 128, 0, 1)"
                    }
                    onChange={(color) =>
                      dispatch({
                        type: "SET_FLUID_SEGMENTATION_COLOR",
                        label: "PED",
                        color,
                      })
                    }
                  />
                </div> */}
            </div>

            <div
              style={{
                padding: "20px 10px",
                display: "flex",
                justifyContent: "flex-end",
                borderTop: "1px solid #ccc",
                marginTop: "20px",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  try {
                    dispatch({
                      type: "LoadingOverlay_changed",
                      LoadingOverlay: true,
                    });
                    const resp = await saveUserSettings(
                      query.get("user_id"),
                      dicom_data?.settings
                    );
                    setRespMessage({
                      error: false,
                      message: "Settings saved successfully!",
                    });
                  } catch (error) {
                    console.error("Error saving settings:", error);
                    setRespMessage({
                      error: true,
                      message: "Failed to save settings. Please try again.",
                    });
                  } finally {
                    dispatch({
                      type: "LoadingOverlay_changed",
                      LoadingOverlay: false,
                    });
                  }
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </Drawer>

        <AlertDialog
          open={respMessage.message === "" ? false : true}
          handleClose={() => {
            setRespMessage({ error: false, message: "" });
          }}
          message={respMessage.message}
          error={respMessage.error}
        />
      </React.Fragment>
    </>
  );
}
